import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HelpCircle, ShieldCheck, Users, Scale } from "lucide-react";

import MainLayout from "@/components/MainLayout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const faqGroups = [
  {
    key: "bewerber",
    title: "FAQ für Bewerber",
    icon: ShieldCheck,
    items: [
      {
        question: "Wer kann Mitglied bei REEXPERTS werden?",
        answer: "Mitglied kann werden, wer im Bereich der Immobilienbewertung, des Bauschadenswesens, des Verkehrswertgutachtens oder in einer sachverständig geprägten angrenzenden Tätigkeit qualifiziert tätig ist. Auch qualifizierte Immobilienmakler mit sachverständigenbezogener Weiterbildung können berücksichtigt werden, sofern die fachliche Eignung und die standesgemäße Einordnung nachgewiesen werden.",
      },
      {
        question: "Welche Unterlagen sind für den Antrag erforderlich?",
        answer: "Erforderlich sind insbesondere Angaben zu Person, Beruf, Ausbildung, Erfahrung und Tätigkeitsschwerpunkten sowie drei Referenzgutachten oder gleichwertige Nachweise. Unvollständige oder nicht plausibel belegte Anträge können zur Nachforderung oder Zurückstellung führen.",
      },
      {
        question: "Besteht ein Anspruch auf Aufnahme?",
        answer: "Ein Anspruch auf Aufnahme besteht nicht. Die Entscheidung erfolgt nach geordnetem Prüfungsverfahren und richtet sich nach fachlicher Eignung, Verbandswürdigkeit und Vereinbarkeit mit dem Qualitätsverständnis der Vereinigung.",
      },
      {
        question: "Wann wird die REEXPERTS-Kennung vergeben?",
        answer: "Die Kennung wird erst nach positiver Aufnahmeentscheidung und administrativer Freigabe vergeben. Sie ist Teil der qualitätsbezogenen Außendarstellung und steht unter dem Vorbehalt fortbestehender Eignung.",
      },
    ],
  },
  {
    key: "mitglieder",
    title: "FAQ für Mitglieder",
    icon: Users,
    items: [
      {
        question: "Welche Rechte vermittelt die Mitgliedschaft?",
        answer: "Die Mitgliedschaft vermittelt die vorgesehenen Verbandsleistungen, die Sichtbarkeit im qualitätsbezogenen Auftritt, die Führung der Kennung nach Freigabe sowie die Teilnahme an Weiterbildungs- und Informationsangeboten.",
      },
      {
        question: "Wie darf das Siegel genutzt werden?",
        answer: "Das Siegel darf ausschließlich in der freigegebenen Form und nur solange verwendet werden, wie die Berechtigung fortbesteht. Unzulässig sind insbesondere Veränderungen, irreführende Kontexte oder jede Nutzung trotz Ruhens, Entzugs oder Ausschlusses.",
      },
      {
        question: "Wann wird der Mitgliedsbeitrag fällig?",
        answer: "Die Beitragspflicht entsteht mit positiver Aufnahmeentscheidung und administrativer Freigabe im vorgesehenen Verfahren. Fälligkeit, Mahnung, Zahlungsstatus und etwaige Folgen des Rückstands sind in der Beitragsordnung näher geregelt.",
      },
      {
        question: "Wie erfolgen Austritt oder Statusänderungen?",
        answer: "Austritte, Stornierungen oder sonstige Statusänderungen sind geordnet über die vorgesehenen Wege einzureichen. Laufende Verfahren, offene Forderungen oder dokumentationsrelevante Vorgänge bleiben hiervon unberührt, soweit rechtlich zulässig.",
      },
    ],
  },
  {
    key: "mandanten",
    title: "FAQ für Mandanten",
    icon: Scale,
    items: [
      {
        question: "Was bedeutet die Kennzeichnung REEXPERTS?",
        answer: "Die Kennzeichnung weist auf eine qualitätsbezogene Einordnung innerhalb der Vereinigung hin. Sie stellt keine staatliche Bestellung kraft eigener Erklärung dar, sondern verweist auf eine dokumentierte Prüfung und Freigabe nach den internen Standards der Vereinigung.",
      },
      {
        question: "Wie kann eine Beschwerde eingereicht werden?",
        answer: "Beschwerden können über die Beschwerdestelle mit Beschreibung des Sachverhalts und Angabe der relevanten Referenzen eingereicht werden. Jeder Vorgang wird erfasst, vorsortiert und in geordneter Weise bearbeitet.",
      },
      {
        question: "Gibt es eine freie Schiedsstelle?",
        answer: "Soweit angezeigt, kann eine freie Schiedsstelle unterstützend eingebunden werden. Die Vereinigung strebt eine sachliche, nachvollziehbare und dokumentierte Verfahrensführung an.",
      },
      {
        question: "Wie wird Qualität innerhalb der Vereinigung sichergestellt?",
        answer: "Die Qualitätssicherung erfolgt über Aufnahmeprüfung, Referenzgutachten, Weiterbildungsrahmen, Verfahrensregeln, Dokumentation, Beschwerdebearbeitung und gegebenenfalls Maßnahmen bis hin zu Entzug oder Ausschluss.",
      },
    ],
  },
];

export default function FaqPage({ session, onLogout }) {
  return (
    <MainLayout
      session={session}
      onLogout={onLogout}
      pageTitle="FAQ"
      pageDescription="Häufig gestellte Fragen für Bewerber, Mitglieder und Mandanten von REEXPERTS."
    >
      <div className="bg-[#111111] text-[#f7f1e8]">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-6 py-14 lg:px-8 lg:py-20">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="grid gap-8 xl:grid-cols-[0.95fr_1.05fr]" data-testid="faq-hero-section">
            <div className="space-y-6 rounded-[2rem] border border-[#d4af37]/25 bg-[#141414] p-8">
              <Badge className="w-fit rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]" data-testid="faq-hero-badge">
                <span className="mr-3 inline-flex items-center gap-1">
                  <span className="h-2 w-4 rounded-full bg-[#2f2f2f]" />
                  <span className="h-2 w-4 rounded-full bg-[#a8171b]" />
                  <span className="h-2 w-4 rounded-full bg-[#d4af37]" />
                </span>
                FAQ für Bewerber, Mitglieder und Mandanten
              </Badge>
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#b99d6b]" data-testid="faq-hero-kicker">Geordnete Antworten auf die wesentlichen Fragen</p>
                <h1 className="mt-4 font-serif text-4xl leading-[1.04] text-white lg:text-5xl" data-testid="faq-hero-heading">
                  Häufig gestellte Fragen im Verbandssystem von REEXPERTS.
                </h1>
              </div>
              <p className="text-base leading-relaxed text-[#cbc2b7]" data-testid="faq-hero-description">
                Die FAQ gibt einen geordneten Überblick über Aufnahme, Mitgliedschaft, Kennzeichnung, Beiträge, Beschwerdewege und die qualitätsbezogene Einordnung innerhalb der Vereinigung. Sie dient der Orientierung, ersetzt jedoch nicht die jeweils einschlägigen Satzungs-, Ordnungs- oder Richtlinientexte.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/apply" data-testid="faq-hero-apply-link">
                  <Button className="rounded-full bg-[#e5d3b3] text-[#121212] hover:bg-[#f4e7d3]">Mitgliedschaft beantragen</Button>
                </Link>
                <Link to="/beschwerde" data-testid="faq-hero-complaint-link">
                  <Button variant="outline" className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5">Beschwerdestelle öffnen</Button>
                </Link>
              </div>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {faqGroups.map((group) => {
                const Icon = group.icon;
                return (
                  <Card key={group.key} className="border-white/10 bg-[#171717]" data-testid={`faq-group-card-${group.key}`}>
                    <CardContent className="space-y-4 p-6">
                      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#d4af37]/30 bg-[#e5d3b3]/10 text-[#e5d3b3]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <p className="font-semibold text-white">{group.title}</p>
                      <p className="text-sm leading-relaxed text-[#c7bfb3]">{group.items[0].answer}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.section>

          <section className="grid gap-6 xl:grid-cols-3" data-testid="faq-groups-section">
            {faqGroups.map((group) => (
              <Card key={group.key} className="border-white/10 bg-[#171717]" data-testid={`faq-accordion-card-${group.key}`}>
                <CardContent className="p-6">
                  <div className="mb-5 flex items-center gap-3">
                    <HelpCircle className="h-5 w-5 text-[#e5d3b3]" />
                    <h2 className="font-serif text-[2rem] text-white" data-testid={`faq-group-title-${group.key}`}>{group.title}</h2>
                  </div>
                  <Accordion type="single" collapsible className="w-full">
                    {group.items.map((item, index) => (
                      <AccordionItem key={item.question} value={`${group.key}-${index}`} className="border-white/10">
                        <AccordionTrigger className="text-left text-white" data-testid={`faq-trigger-${group.key}-${index + 1}`}>{item.question}</AccordionTrigger>
                        <AccordionContent className="text-sm leading-relaxed text-[#d8d0c5]" data-testid={`faq-content-${group.key}-${index + 1}`}>{item.answer}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </section>
        </div>
      </div>
    </MainLayout>
  );
}
